/**
 * Cross-store orchestration (Perf 3.1.b Step 3).
 *
 * Centralizes the multi-store side effects that the auth store triggers on
 * logout. Sibling stores are reached through a RUNTIME `require('@/store')`
 * so importing this module (and therefore `store/auth.js`) does not pull the
 * sibling-store graph at module-eval time — that would re-create the circular
 * init the barrel→auth re-export is designed to avoid.
 *
 * As each sibling store becomes its own leaf (Steps 4–7) the destructured
 * `require('@/store')` below gets repointed to the specific leaf module.
 */
import { clearLastRoute } from '@/lib/lastRoute';

export function clearAllStoresOnLogout() {
  // Cancel Deck's queued saves before other cleanup can fail or a new
  // account can begin loading its workspace.
  require('./deck').useDeckStore.getState().actions.clearDecks();
  try {
    require('@/lib/discoveryContinuationCache').clearDiscoveryContinuationCache();
  } catch (error) {
    console.error('Error clearing discovery continuation cache:', error);
  }
  try {
    require('@/lib/plugins/pluginStore').clearPluginCache();
  } catch (error) {
    console.error('Error resetting plugin store:', error);
  }
  try {
    require('./coworker').useCoworkerStore.getState().reset();
  } catch (error) {
    console.error('Error resetting coworker store:', error);
  }
  try {
    const {
      useCollectionStore,
      useChatStore,
      useRouteStore,
      useAgentStore,
      useModelStore,
      useCompanyStore,
    } = require('@/store');
    // Clear all other stores (do not call clearUI — it resets persisted
    // preferences for every login).
    useCollectionStore.getState().clearCollections();
    useChatStore.getState().clearChats();
    useRouteStore.getState().actions.clearRoutes();
    useAgentStore.getState().clearAgents();
    useModelStore.getState().clearModels();
    useCompanyStore.getState().clearCompanies();
    // Drop any remembered last route / scroll position so a fresh login
    // (possibly a different user) starts at home, not the prior session's
    // chat. Cross-user chat routes would 404/403 and self-heal anyway,
    // but clearing avoids the flash entirely.
    clearLastRoute();
  } catch (error) {
    console.error('Error clearing stores:', error);
  }
}
